import React from 'react';

type CalendarView = 'day' | 'week';

interface StaffMember {
  id: string;
  name: string;
  role: string;
}

interface CalendarControlsProps {
  currentDate: Date;
  view: CalendarView;
  formatDateRange: () => string;
  onNavigatePrev: () => void;
  onNavigateToday: () => void;
  onNavigateNext: () => void;
  onSetView: (view: CalendarView) => void;
  staffMembers?: StaffMember[];
  selectedStaffId?: string | null;
  onStaffChange?: (staffId: string | null) => void;
}

const CalendarControls: React.FC<CalendarControlsProps> = ({
  view,
  formatDateRange,
  onNavigatePrev,
  onNavigateToday,
  onNavigateNext,
  onSetView,
  staffMembers,
  selectedStaffId,
  onStaffChange
}) => {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2">
        <button
          onClick={onNavigatePrev}
          className="p-2 hover:bg-gray-100 rounded"
          aria-label="Previous"
        >
          ◀
        </button>
        <button
          onClick={onNavigateToday}
          className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50 text-sm"
        >
          Today
        </button>
        <button
          onClick={onNavigateNext}
          className="p-2 hover:bg-gray-100 rounded"
          aria-label="Next"
        >
          ▶
        </button>
        <h2 className="text-lg font-medium text-[#344854] ml-2">
          {formatDateRange()}
        </h2>
      </div>

      <div className="flex items-center gap-4">
        {/* Staff Selector */}
        {staffMembers && onStaffChange && (
          <div className="flex items-center gap-2">
            <label htmlFor="staff-selector" className="text-sm font-medium text-gray-700 whitespace-nowrap">
              Staff:
            </label>
            <select
              id="staff-selector"
              className="px-2 py-1 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#3A5E63] focus:border-[#3A5E63]"
              value={selectedStaffId || ''}
              onChange={(e) => onStaffChange(e.target.value || null)}
            >
              {staffMembers.map(staff => (
                <option key={staff.id} value={staff.id}>
                  {staff.name} ({staff.role})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* View Toggle */}
        <div className="inline-flex rounded-md shadow-sm" role="group">
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium rounded-l-lg border ${
              view === 'day'
                ? 'bg-[#3A5E63] text-white border-[#3A5E63]'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => onSetView('day')}
          >
            Day
          </button>
          <button
            type="button"
            className={`px-4 py-2 text-sm font-medium rounded-r-lg border ${
              view === 'week'
                ? 'bg-[#3A5E63] text-white border-[#3A5E63]'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
            onClick={() => onSetView('week')}
          >
            Week
          </button>
        </div>
      </div>
    </div>
  );
};

export default CalendarControls; 